import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, Users, DoorOpen, Wallet, Clock3, AlertCircle, LibraryBig, CalendarCheck, CreditCard } from "lucide-react";
import { PageHeader, StatCard, EmptyState } from "../components/Primitives";
import { api, errorText, inr, formatDate } from "../lib/api";

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/dashboard")
      .then((response) => setData(response.data))
      .catch((err) => setError(errorText(err)));
  }, []);

  const metrics = data?.metrics || {};
  const seats = metrics.seats || 0;
  const occupancy = seats ? Math.round(((metrics.occupied_seats || 0) / seats) * 100) : 0;

  const isAdmin = user.role === "MAIN_ADMIN";

  const action = isAdmin ? (
    <button className="button button-primary" onClick={() => navigate("/libraries")} data-testid="dashboard-primary-action">
      Manage libraries <ArrowRight size={16} />
    </button>
  ) : (
    <button className="button button-primary" onClick={() => navigate("/students")} data-testid="dashboard-primary-action">
      Add student <ArrowRight size={16} />
    </button>
  );

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow={isAdmin ? "MAIN ADMINISTRATION" : "LIBRARY OPERATIONS"}
        title={isAdmin ? "Good morning, Admin." : "Today, at a glance."}
        description={isAdmin ? "A clear view across every library in your network." : "The numbers that help your team make the next decision."}
        action={action}
      />
      {error ? <div className="error-message" data-testid="dashboard-error-message">{error}</div> : null}

      <div className="stat-grid">
        {isAdmin ? (
          <>
            <StatCard label="Active libraries" value={metrics.active_libraries ?? "—"} detail={`${metrics.libraries ?? 0} total · ${metrics.trial_libraries ?? 0} on trial`} icon={<LibraryBig size={18} />} />
            <StatCard label="Total students" value={metrics.students ?? "—"} detail="Across every library" tone="green" icon={<Users size={18} />} />
            <StatCard label="Occupied seats" value={metrics.occupied_seats ?? "—"} detail={`${metrics.available_seats ?? 0} available`} tone="orange" icon={<DoorOpen size={18} />} />
            <StatCard label="Platform revenue" value={inr(metrics.platform_revenue)} detail={`${metrics.pending_invoices ?? 0} pending invoices`} tone="dark" icon={<Wallet size={18} />} />
          </>
        ) : (
          <>
            <StatCard label="Active students" value={metrics.students ?? "—"} detail={`${metrics.expiring_soon ?? 0} expiring in 15 days`} icon={<Users size={18} />} />
            <StatCard label="This month collection" value={inr(metrics.month_collection)} detail={`Last month ${inr(metrics.last_month_collection)}`} tone="green" icon={<Wallet size={18} />} />
            <StatCard label="This month admissions" value={metrics.month_admissions ?? 0} detail={`Last month ${metrics.last_month_admissions ?? 0}`} tone="orange" icon={<Users size={18} />} />
            <StatCard label="Pending bills" value={metrics.pending_bills ?? "—"} detail={`${metrics.overdue_bills ?? 0} overdue`} tone="dark" icon={<CreditCard size={18} />} />
          </>
        )}
      </div>

      {!isAdmin ? (
        <div className="stat-grid" style={{ marginTop: 0 }}>
          <StatCard label="Today's collection" value={inr(metrics.today_collection)} detail="Payments today" icon={<Wallet size={18} />} />
          <StatCard label="Available seats" value={metrics.available_seats ?? "—"} detail={`${metrics.occupied_seats ?? 0} occupied`} tone="green" icon={<DoorOpen size={18} />} />
          <StatCard label="Attendance today" value={metrics.today_attendance ?? 0} detail="Marked present" tone="orange" icon={<CalendarCheck size={18} />} />
          <StatCard label="Active shifts" value={metrics.shifts ?? 0} detail="Running today" tone="dark" icon={<Clock3 size={18} />} />
        </div>
      ) : null}

      <div className="dashboard-grid">
        <section className="content-card occupancy-card">
          <div className="card-heading">
            <div>
              <span className="card-kicker">SEAT OCCUPANCY</span>
              <h2>Space at a glance</h2>
            </div>
            <span className="live-badge"><span />Live</span>
          </div>
          <div className="occupancy-main">
            <div className="donut" style={{ "--value": `${occupancy}%` }} data-testid="occupancy-donut">
              <div>
                <strong>{occupancy}%</strong>
                <span>occupied</span>
              </div>
            </div>
            <div className="legend">
              <div><i className="legend-blue" /><span>Occupied</span><strong>{metrics.occupied_seats ?? 0}</strong></div>
              <div><i className="legend-light" /><span>Available</span><strong>{metrics.available_seats ?? 0}</strong></div>
              <div><i className="legend-shift" /><span>Shifts</span><strong>{metrics.shifts ?? 0}</strong></div>
              <div><i className="legend-attend" /><span>Attendance today</span><strong>{metrics.today_attendance ?? 0}</strong></div>
            </div>
          </div>
          <button className="card-link" onClick={() => navigate(isAdmin ? "/libraries" : "/seats")} data-testid="dashboard-seats-link">
            {isAdmin ? "View libraries" : "View seat map"} <ArrowRight size={15} />
          </button>
        </section>

        <section className="content-card activity-card">
          <div className="card-heading">
            <div>
              <span className="card-kicker">RECENT ACTIVITY</span>
              <h2>What&rsquo;s happening</h2>
            </div>
            <span className="live-badge" data-testid="activity-status">Latest activity</span>
          </div>
          {data?.recent_activity?.length ? (
            <div className="activity-list">
              {data.recent_activity.map((item) => (
                <div className="activity-item" key={item.id}>
                  <span className="activity-dot" />
                  <div>
                    <strong>{item.action} {item.entity.toLowerCase()}</strong>
                    <small>{item.user_name || "Workspace user"} · {formatDate(item.created_at)}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={<AlertCircle size={22} />} title="Your activity trail is ready" hint="Important actions will appear here as your team works." testId="activity-empty-state" />
          )}
        </section>
      </div>

      {!isAdmin ? (
        <section className="content-card recent-payments-card" style={{ marginTop: 16 }}>
          <div className="card-heading">
            <div>
              <span className="card-kicker">RECENT PAYMENTS</span>
              <h2>Latest collections</h2>
            </div>
            <button className="button button-outline" onClick={() => navigate("/payments")} data-testid="view-payments-link">
              View all <ArrowRight size={15} />
            </button>
          </div>
          {data?.recent_payments?.length ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Receipt</th><th>Student</th><th>Amount</th><th>Mode</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {data.recent_payments.map((payment) => (
                    <tr key={payment.id} data-testid={`recent-payment-${payment.id}`}>
                      <td>{payment.receipt_number}</td>
                      <td>{payment.student_name}</td>
                      <td>{inr(payment.amount)}</td>
                      <td>{payment.payment_mode}</td>
                      <td>{formatDate(payment.payment_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon={<Wallet size={22} />} title="No payments yet" hint="Payments recorded today will appear here." />
          )}
        </section>
      ) : null}
    </div>
  );
}
